/**
 * ============================================================
 * 🔐 AUTH UI CONTEXT — ENTERPRISE UI STATE MANAGEMENT
 * ============================================================
 *
 * 🎯 RESPONSABILITÉ UNIQUE
 * ------------------------------------------------------------
 * Gérer UNIQUEMENT l’état d’interface lié à l’authentification.
 *
 * ⚠️ IMPORTANT :
 * Ce contexte NE GÈRE PAS :
 * - la connexion Firebase
 * - l'utilisateur
 * - le profil
 * - les rôles
 *
 * Il gère uniquement :
 * - L’ouverture / fermeture de la modal d’auth
 * - Les intentions UI liées à l’authentification
 *
 *
 * 🧠 POURQUOI CE FICHIER EXISTE ?
 * ------------------------------------------------------------
 * On sépare volontairement :
 *
 * 🔐 AuthContext   → LOGIQUE MÉTIER (sécurité, session)
 * 🎨 AuthUIContext → LOGIQUE INTERFACE (modals, affichage)
 *
 * 👉 Cela évite :
 * - Couplage UI / sécurité
 * - Code fragile
 * - Effets de bord
 * - Context monolithique
 *
 * 👉 Cela permet :
 * - Évolutivité
 * - Testabilité
 * - Lisibilité
 * - Architecture propre type SaaS
 *
 * ============================================================
 */

import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { readAuthBrowserHandoff, clearAuthBrowserHandoff } from "./authBrowserHandoff";
import { readShareAttribution, trackConversionEvent } from "../analytics/conversionAnalytics";

/* ============================================================
   1️⃣ CRÉATION DU CONTEXT
============================================================ */

/**
 * Conteneur global pour l’état UI d’authentification.
 *
 * On initialise à null volontairement
 * pour forcer son usage dans le Provider.
 */
const AuthUIContext = createContext(null);
const AUTH_INTENT_STORAGE_KEY = "karango.auth.pending-intent";

function readStoredAuthIntent() {
  try {
    const value = JSON.parse(window.sessionStorage.getItem(AUTH_INTENT_STORAGE_KEY));
    if (!value?.type || Date.now() - Number(value.createdAt || 0) > 30 * 60 * 1000) {
      window.sessionStorage.removeItem(AUTH_INTENT_STORAGE_KEY);
      return null;
    }
    return value;
  } catch (_error) {
    return null;
  }
}

function storeAuthIntent(intent) {
  try {
    if (intent) window.sessionStorage.setItem(AUTH_INTENT_STORAGE_KEY, JSON.stringify(intent));
    else window.sessionStorage.removeItem(AUTH_INTENT_STORAGE_KEY);
  } catch (_error) {}
}

/* ============================================================
   2️⃣ PROVIDER
============================================================ */

/**
 * AuthUIProvider
 *
 * Doit entourer ton application (ou au moins
 * la partie qui utilise la modal d'auth).
 *
 * Exemple dans index.js :
 *
 * <AuthUIProvider>
 *   <App />
 * </AuthUIProvider>
 */
export function AuthUIProvider({ children }) {
  const [browserHandoff] = useState(() => readAuthBrowserHandoff(window.location));

  /* ==========================================================
     🎛 STATE INTERNE
  ========================================================== */

  /**
   * isAuthModalOpen
   * --------------------------------------------------------
   * Boolean contrôlant l'affichage de la modal d’authentification.
   *
   * true  → modal visible
   * false → modal fermée
   */
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(Boolean(browserHandoff));
  const [pendingAuthIntent, setPendingAuthIntent] = useState(() =>
    browserHandoff ? browserHandoff.intent : readStoredAuthIntent()
  );

  useEffect(() => {
    if (!browserHandoff) return;
    storeAuthIntent(browserHandoff.intent);
    clearAuthBrowserHandoff(window);
  }, [browserHandoff]);


  /* ==========================================================
     🧩 ACTIONS UI
  ========================================================== */

  /**
   * Ouvre explicitement la modal
   */
  const openAuthModal = useCallback((intent = null) => {
    if (intent?.type) {
      const nextIntent = { ...intent, createdAt: Date.now() };
      setPendingAuthIntent(nextIntent);
      storeAuthIntent(nextIntent);
      if (intent.type === "join_event") {
        trackConversionEvent("shared_event_auth_started", {
          event_id: intent.eventId,
          share_source: readShareAttribution().shareSource || "direct",
        });
      }
    }
    setIsAuthModalOpen(true);
  }, []);

  /**
   * Ferme explicitement la modal
   */
  const closeAuthModal = useCallback(() => {
    setIsAuthModalOpen(false);
  }, []);

  const consumeAuthIntent = useCallback(() => {
    setPendingAuthIntent(null);
    storeAuthIntent(null);
  }, []);

  /**
   * Toggle intelligent
   *
   * Permet :
   * - bouton unique
   * - logique conditionnelle
   * - future extension (ex: analytics event)
   */
  const toggleAuthModal = useCallback(() => {
    setIsAuthModalOpen((prev) => !prev);
  }, []);


  /* ==========================================================
     📦 VALEUR EXPOSÉE AU RESTE DE L’APP
  ========================================================== */

  /**
   * useMemo évite que le contexte change inutilement
   * à chaque re-render.
   *
   * Important pour performances si app large.
   */
  const value = useMemo(() => ({
    isAuthModalOpen,

    // actions disponibles
    openAuthModal,
    closeAuthModal,
    toggleAuthModal,
    pendingAuthIntent,
    consumeAuthIntent,
  }), [
    isAuthModalOpen,
    openAuthModal,
    closeAuthModal,
    toggleAuthModal,
    pendingAuthIntent,
    consumeAuthIntent,
  ]);


  /* ==========================================================
     RENDER PROVIDER
  ========================================================== */

  return (
    <AuthUIContext.Provider value={value}>
      {children}
    </AuthUIContext.Provider>
  );
}


/* ============================================================
   3️⃣ CUSTOM HOOK
============================================================ */

/**
 * useAuthUI()
 *
 * Hook sécurisé pour accéder au contexte.
 *
 * Avantages :
 * - Empêche utilisation hors Provider
 * - Erreur claire en développement
 */
export function useAuthUI() {

  const context = useContext(AuthUIContext);

  if (!context) {
    throw new Error(
      "useAuthUI must be used inside AuthUIProvider"
    );
  }

  return context;
}

// Variante sans exigence de Provider : renvoie null hors de l'application
// (ecrans testes isolement), au lieu de lever une erreur.
export function useOptionalAuthUI() {
  return useContext(AuthUIContext);
}
